<template>
  <div class="main-page-container">
    <PageHeader className="d-flex align-items-center" :title="title">
      <b-button
        @click="startSessionModal()"
        pill
        class="d-flex align-items-center justify-content-center text-center session-btn"
        variant="primary"
        size="sm"
      >
        {{ $t("emergencyList.form.startSession") }}
      </b-button>
    </PageHeader>

    <!-- grid -->
    <section
      class="emergency-list-table emergency-sessions-white-bg-sec"
      style="background-color: white"
    >
      <DxDataGrid
        id="gridEmergencySessions"
        :word-wrap-enabled="true"
        :data-source="rowsData"
        :ref="dataGridRefName"
        :remote-operations="false"
        :showBorders="false"
        :columnAutoWidth="true"
        :allowColumnReordering="true"
        :allowColumnResizing="true"
        :rowAlternationEnabled="false"
        :showColumnLines="true"
        :showRowLines="true"
        :repaint-changes-only="true"
        :two-way-binding-enabled="true"
        :no-data-text="gridEmptyMessage"
        @editor-preparing="onEditorPreparing($event)"
        @content-ready="contentReady($event)"
        @data-error-occurred="onDataErrorOccurred($event)"
        :rtl-enabled="rtlEnabled"
      >
        <DxScrolling mode="standard" :scroll-by-content="rtlEnabled" :use-native="!rtlEnabled" />
        <DxPaging :page-size="50" />
        <DxPager
          :visible="rowsData.length ? true : false"
          :allowed-page-sizes="[5, 10, 25, 50, 100]"
          :display-mode="'full'"
          :show-page-size-selector="true"
          :show-info="true"
          :show-navigation-buttons="true"
          :infoText="rtlEnabled ?  'صفحة {0} من {1} ({2} إجمالي العناصر)' : ''"
        />

        <DxStateStoring
          :enabled="true"
          type="custom"
          :customSave="customSave"
          :customLoad="customLoad"
        />
        <DxFilterRow :visible="true" />
        <DxSorting mode="single" />
        <DxHeaderFilter :visible="true" />

        <DxColumnChooser
          :enabled="true"
          mode="select"
          :title="$t('common.grid.columnChooser')"
        />

        <DxExport :enabled="true" :allow-export-selected-data="false" />

        <DxToolbar>
          <DxItem location="before" template="columnButtonTemplate" />
          <DxItem location="before" template="exportButtonTemplate" />
          <DxItem location="before" template="clearAllTemplate" />
        </DxToolbar>

        <DxColumn
          data-field="name"
          :caption="$t('emergencySessionsList.grid.sessionName')"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :visible="true"
          cell-template="session-name-cell"
        />

        <DxColumn
          data-field="started_by"
          :caption="$t('emergencySessionsList.grid.startedBy')"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :visible="true"
        />

        <DxColumn
          data-field="started_at"
          format="yyyy/MM/dd HH:mm:ss"
          :caption="$t('emergencySessionsList.grid.startedAt')"
          :allow-editing="false"
          :visible="true"
          :allow-filtering="true"
          :filter-operations="allowedOperations"
        />

        <DxColumn
          data-field="ended_by"
          :caption="$t('emergencySessionsList.grid.endedBy')"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :visible="true"
        />

        <DxColumn
          data-field="ended_at"
          :caption="$t('emergencySessionsList.grid.endedAt')"
          format="yyyy/MM/dd HH:mm:ss"
          :allow-editing="false"
          :visible="true"
          :allow-filtering="true"
          :filter-operations="allowedOperations"
          :allow-header-filtering="false"
        />

        <DxColumn
          data-field="areas"
          :caption="$t('emergencySessionsList.grid.areas')"
          :width="201"
          :allow-filtering="false"
          :allow-header-filtering="true"
          :allow-editing="false"
          :visible="true"
        />

        <DxColumn
          data-field="type"
          :caption="$t('emergencySessionsList.grid.type')"
          :allow-header-filtering="false"
          :visible="true"
          :width="200"
          :allow-filtering="true"
          :filter-operations="allowedOperations"
        />

        <DxColumn
          data-field="actions"
          :caption="$t('common.grid.actions')"
          :allow-filtering="false"
          :allow-exporting="false"
          :allow-editing="false"
          :visible="true"
          :allow-sorting="false"
          cell-template="action-session-cell"
        />

        <template #exportButtonTemplate>
          <DxDropDownButton
            class="export-dropdown"
            :items="buttonData"
            :text="$t('common.grid.export')"
            @item-click="onItemClick"
          />
        </template>

        <template #columnButtonTemplate>
          <button class="custom-columns" @click="dataGrid.showColumnChooser()">
            <span class="custom-text">{{ $t("common.grid.columns") }} </span>
            <span class="custom-icon">
              <i class="dx-icon dx-icon-spindown dx-icon-right"></i>
            </span>
          </button>
        </template>

        <template #action-session-cell="{ data }">
          <b-button
            v-if="data.data.status == 'active'"
            @click="viewSessionData(data.data.id, data.data.name)"
            class="d-flex align-items-center text-center join-btn custom-action-grid"
            >{{ $t("common.join") }}</b-button
          >
        </template>

        <template #session-name-cell="{ data }">
          <div
            @click="viewSessionData(data.data.id, data.data.name)"
            v-if="data.data.status != 'active'"
            class="inactive-session"
          >
            <span class="cursor-pointer">{{ data.data.name }}</span>
          </div>

          <div
            @click="viewSessionData(data.data.id, data.data.name)"
            v-else
            class="text-green bold-cell active-session"
          >
            <span class="cursor-pointer">
              <svg
                style="margin-inline-end: 9px"
                width="9"
                height="9"
                viewBox="0 0 9 9"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="4.5" cy="4.5" r="4.5" fill="#34C38F" />
              </svg>
              {{ data.data.name }}
            </span>
          </div>
        </template>

        <template #clearAllTemplate>
          <div v-if="showClearAll" class="clear-cols d-flex align-items-center">
            <div class="toolbar-custom-line"></div>
            <Button
              :text="$t('common.grid.clearFilters')"
              @click="reset()"
              styling-mode="outlined"
              type="danger"
              class="clear-CustomColums"
            />
          </div>
        </template>
      </DxDataGrid>
    </section>

    <!-- Start Sessin Modal  -->
    <startSessionForm />
  </div>
</template>

<script>
const dataGridRefName = "dataGridEmergencySessions";
import { mapActions, mapGetters } from "vuex";
import PageHeader from "../../../components/page-header.vue";
import { exportDataGrid } from "devextreme/excel_exporter";
import { exportDataGrid as exportDataGridToPdf } from "devextreme/pdf_exporter";
import { Workbook } from "exceljs";
import DxDropDownButton from "devextreme-vue/drop-down-button";
import saveAs from "file-saver";
import { jsPDF } from "jspdf";
import { emergencySessionsListColumns } from "@/utils/dataSource";
import startSessionForm from "@/components/emergency/startSession";
import moment from "moment";
import "jspdf-autotable";
import { default as Button } from "devextreme-vue/button";
import amiriFont from "@/assets/fonts/Amiri-Regular.ttf"; // Load the Cairo font

import DxDataGrid, {
  DxColumn,
  DxFilterRow,
  DxHeaderFilter,
  DxColumnChooser,
  DxExport,
  DxStateStoring,
  DxToolbar,
  DxItem,
  DxPaging,
  DxPager,
  DxSorting,
  DxScrolling,
} from "devextreme-vue/data-grid";
export default {
  name: "EmergencySessionsList",
  title: "Emergency Sessions",
  components: {
    PageHeader,
    DxDataGrid,
    DxColumn,
    DxFilterRow,
    DxHeaderFilter,
    DxColumnChooser,
    DxExport,
    DxStateStoring,
    DxToolbar,
    DxItem,
    DxDropDownButton,
    DxPaging,
    DxPager,
    startSessionForm,
    DxSorting,
    Button,
    DxScrolling,
  },
  data() {
    return {
      // title : "Emergency Sessions",
      gridEmptyMessage : this.$t('common.grid.loadingData'),
      rtlEnabled: false,
      dataGridRefName,
      allowedOperations: ["contains"],
      selectedOperation: "contains", // default icon
      isServerSideExport: false,
      buttonData: ["Excel", "CSV", "PDF"],
      emergencySessionsListColumns: emergencySessionsListColumns,
      statusHeaderFilter: [],
      areaHeaderFilter: [],
      userTypeHeaderFilter: [],
      isDisabled: false,
      loggedUser: "",
      showClearAll: false,
      emergencySessionsListData: [],
    };
  },
  methods: {
    ...mapActions("grid", [
      "onTableHeader",
      "updateTableHeaders",
      "createTableHeaders",
      "onSetLoading",
    ]),
    ...mapActions("emergency", [
      "fetchGridEmergencySessionsListData",
      "getAllEmergencySessions",
      // "fetchSessionData",
    ]),
    async customLoad() {
      if (this.userEmail !== "lt_techteam@visipoint.com") {
        await this.onTableHeader({
          url: "/all-emergencies-sessions",
        });
        let value = this.getTableHeaders;
        console.log("customLoad");
        return value;
      }
    },
    customSave(gridState) {
      if (this.userEmail !== "lt_techteam@visipoint.com") {
        gridState.pageIndex = 0;
        gridState.pageSize = 50;
        let value = JSON.stringify(gridState);
        value = value.replace(',"selectedRowKeys":[]', "");

        if (!this.getTableHeaders) {
          this.createTableHeaders({
            url: "/all-emergencies-sessions",
            headers: value,
            filters: "[]",
          });
        } else if (value != JSON.stringify(this.getTableHeaders)) {
          this.updateTableHeaders({
            url: "/all-emergencies-sessions",
            headers: value,
            filters: "[]",
          });
        }
        console.log(
          "customSave value",
          value != JSON.stringify(this.getTableHeaders)
        );
      }
    },
    async onItemClick(e) {
      e.rtlEnabled = this.rtlEnabled;
      if (e.itemData == "Excel") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Main sheet");
        await exportDataGrid({
          component: this.dataGrid,
          worksheet: worksheet,
          customizeCell: function (options) {
            const excelCell = options;
            excelCell.font = { name: "Arial", size: 12 };
            excelCell.alignment = { horizontal: "left" };
          },
        }).then(function () {
          workbook.xlsx.writeBuffer().then(function (buffer) {
            saveAs(
              new Blob([buffer], { type: "application/octet-stream" }),
              "Emergency Sessions List.xlsx"
            );
          });
        });
        // e.cancel = true;
      } else if (e.itemData == "CSV") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Main sheet");

        await exportDataGrid({
          component: this.dataGrid,
          worksheet: worksheet,
          customizeCell: function (options) {
            const excelCell = options;
            excelCell.font = { name: "Arial", size: 12 };
            excelCell.alignment = { horizontal: "left" };
          },
        }).then(function () {
          workbook.csv.writeBuffer().then(function (buffer) {
            saveAs(
              new Blob([buffer], { type: "application/octet-stream" }),
              "Emergency Sessions List.csv"
            );
          });
        });
      } else if (e.itemData == "PDF") {
        const doc = new jsPDF('landscape');

        // Add custom font
        doc.addFont(amiriFont, "Amiri", "normal");
        doc.setFont("Amiri");

        doc.autoTable({ direction: "rtl" });

        await exportDataGridToPdf({
          jsPDFDocument: doc,
          component: this.dataGrid,
          // showHead: "everyPage",
          customizeCell: function (options) {
            const { gridCell, pdfCell } = options;
            if (gridCell.rowType === "data") {
              // data columns style
              pdfCell.styles = {
                font: e.rtlEnabled ? "Amiri" : "Lato",
                fontStyle: "normal",
                fontSize: 8,
                cellWidth: "auto",
                halign: "center",
              };
            } else {
              // header columns style
              pdfCell.styles = {
                font: e.rtlEnabled ? "Amiri" : "Lato",
                fontStyle: "normal",
                fontSize: 8,
                fillColor: "#2680EB",
                textColor: "#FFFFFF",
                cellWidth: "auto",
                halign: "center", 
              };
            }
          },
          customizeTable: (doc) => {
            doc.autoTable({ direction: "rtl" }); // Ensure table follows RTL direction
          },
        }).then(() => {
          const pageCount = doc.internal.getNumberOfPages();
          for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            const pageSize = doc.internal.pageSize;
            const pageWidth = pageSize.width
              ? pageSize.width
              : pageSize.getWidth();
            const pageHeight = pageSize.height
              ? pageSize.height
              : pageSize.getHeight();
            const header = this.$t("sideNavbarMenu.emergencySessions.text");
            const headerWidth = doc.getTextDimensions(header).w;
            const footer = `${this.$t("common.page")} ${i} ${this.$t("common.of")} ${pageCount}`;

            // Header
            doc.text(header, (pageWidth - headerWidth) / 2, 10);

            // Footer
            doc.text(
              footer,
              pageWidth / 2 - doc.getTextWidth(footer) / 2,
              pageHeight - 5,
              { baseline: "bottom" }
            );
          }
          doc.save("Emergency Sessions List.pdf");
        });
      }
    },
    onToggleVisible() {
      this.$bvModal.show("emergencySessionsListColumns");
    },
    contentReady(e) {
      let sortedExp = this.getSortedColumns();
      let filterExp = e.component.getCombinedFilter();
      if (filterExp || sortedExp) {
        this.showClearAll = true;
      } else {
        this.showClearAll = false;
      }
      this.setColumnChooserPosition();
    },
    getSortedColumns() {
      let allColumns = Array.from(
        Array(this.dataGrid.columnCount()).keys()
      ).map((index) => this.dataGrid.columnOption(index));
      allColumns = allColumns.find((col) => col.sortIndex != null);
      return allColumns;
    },
    setColumnChooserPosition() {
      var columnChooserView = this.dataGrid.getView("columnChooserView");
      if (!columnChooserView._popupContainer) {
        columnChooserView._initializePopupContainer();
        columnChooserView.render();
      }
      columnChooserView._popupContainer.option("position", {
        of: this.dataGrid.element(),
        my: this.rtlEnabled ? "right top" : "left top",
        at: this.rtlEnabled ? "right top" : "left top",
        offset: "0 50",
      });
    },
    viewSessionData(id, name) {
      document.getElementById("preloader").style.display = "block";
      document.getElementById("status").style.display = "block";
      this.$router.push({ path: `/emergency-sessions/${name}/${id}` });
      // this.fetchSessionData({ id, name, redirect: true });
    },
    startSessionModal() {
      this.$bvModal.show("startSessionModal");
      setTimeout(() => {
        const selectAllText = document.querySelector(
          ".dx-treeview-select-all-item .dx-checkbox-text"
        );
        if (selectAllText) selectAllText.innerHTML = "All";
      }, 110);
    },
    onEditorPreparing(e) {
      if (e.parentType == "filterRow") {
        e.editorOptions.placeholder = this.$t("common.grid.search");
      }
    },
    fetchTableHeaders() {
      this.onTableHeader({
        url: "/all-emergencies-sessions",
        headers: emergencySessionsListColumns,
      });
    },
    reset() {
      this.dataGrid.clearFilter("row");
      this.dataGrid.clearFilter("header");
      this.dataGrid.clearSorting();
      this.showClearAll = false;
    },
    onDataErrorOccurred(e) {
      console.log("onDataErrorOccurred e", e);
      setTimeout(() => {
        let errorRow = document.querySelector(
          ".dx-datagrid-headers .dx-error-message"
        );
        errorRow.innerHTML = this.$t("common.notes.anErrorOccurredGrid");
      }, 100);
    },
  },
  watch: {
    getTableHeaders(value) {
      console.log("watch", value);
    },
  },
  computed: {
    ...mapGetters("i18N", ["getLanguage"]),
    ...mapGetters("emergency", ["getGridEmergencySessionsListData"]),
    ...mapGetters("dailyLogbook", ["getAreas"]),
    ...mapGetters("grid", ["getTableHeaders", "getTableFilters"]),
    ...mapGetters("authfack", ["userDetails", "currentEntity"]),
    rowsData() {
      // console.log("emergencySessionsListData", this.getGridEmergencySessionsListData);
      return this.emergencySessionsListData &&
        this.emergencySessionsListData.length > 0
        ? this.emergencySessionsListData.map((row) => {
            let startBy = "";
            let endBy = "-";
            let areasString = "";
            for (let i = 0; i < row.areas.length; i++) {
              areasString +=
                row.areas[i].name + (i + 1 < row.areas.length ? ", " : "");
            }

            if (row.user_started) {
              if (row.user_started.email !== "lt_techteam@visipoint.com") {
                startBy = "Admin - " + row.user_started.fullName;
              } else {
                startBy = "Admin - CS";
              }
            }

            if (row.user_ended && row.user_ended != null) {
              if (row.user_ended.email !== "lt_techteam@visipoint.com") {
                endBy = "Admin - " + row.user_ended.fullName;
              } else {
                endBy = "Admin - CS";
              }
            }

            return {
              name: row.name,
              status:
                !row.user_ended || row.user_ended == null
                  ? "active"
                  : "inactive",
              started_by: startBy,
              started_at: moment(new Date(row.start_time + " UTC")).format(
                "YYYY-MM-DD hh:mm:ss A"
              ),
              ended_by: endBy,
              ended_at:
                row.end_time && row.end_time != null
                  ? moment(new Date(row.end_time + " UTC")).format(
                      "YYYY-MM-DD hh:mm:ss A"
                    )
                  : "-",
              type: row.type && row.type != null ? row.type : "-",
              areas: areasString,
              id: row.id,
            };
          })
        : [];
    },
    dataGrid() {
      return this.$refs[this.dataGridRefName].instance;
    },
    userEmail() {
      return this.userDetails && this.userDetails.email;
    },
    title() {
      return this.$t("sideNavbarMenu.emergencySessions.text");
    },
  },
  beforeMount() {
    if (this.getLanguage == "ar") {
      this.rtlEnabled = true;
    }
    this.fetchGridEmergencySessionsListData().then((data) => {
      this.gridEmptyMessage = this.$t('common.grid.noSessionsYet');
      // console.log(data);
      this.emergencySessionsListData = data.data;
      this.dataGrid.endCustomLoading();
    });
  },
  created() {
    setTimeout(() => {
      this.dataGrid.beginCustomLoading();
    }, 100);

    document.addEventListener("mousedown", (e) => {
      if (e.path) {
        let isColumnChooser = false;
        for (let i = 0; i < e.path.length; i++) {
          if (typeof e.path[i].matches == "function") {
            if (e.path[i].matches(".dx-overlay-content")) {
              isColumnChooser = true;
            }
          }
        }
        if (!isColumnChooser) this.dataGrid.hideColumnChooser();
      }
    });

    this.loggedUser = this.userDetails.email;
  },
};
</script>

<style lang="scss" scoped>
button.btn.d-flex.align-items-center.text-center.join-btn.btn-secondary.dx-template-wrapper {
  display: flex;
  background: #34c38f;
  border-radius: 12.5px;
  display: flex;
  justify-content: center;
  border: none;

  font-weight: 700;
  font-size: 16px;
  line-height: 18px;
  text-align: center;
  color: #ffffff;
}
.circle {
  width: 100px;
  height: 100px;
  background: #34c38f;
  border-radius: 50%;
}
.text-success {
  color: #34c38f;
}

p {
  font-size: 12px;
  color: #b9b7b7;
}

img {
  height: 42px;
}

.emergency-list-table {
  tbody {
    color: #495057;
  }
}

.white-bg-sec {
  background-color: white;
  border-radius: 8px;
  //   padding-inline-start: 30px;
  //   padding-top: 32px;
}

button.btn.d-flex.align-items-center.justify-content-center.text-center.session-btn.btn-primary.btn-sm.rounded-pill {
  margin-inline-start: 40px;
  height: 27px;
  width: 127px;

  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
  color: #ffffff;
}
</style>

<style lang="scss">
.no-content {
  background: #ffffff;
  display: flex;
  // align-items: center;
  justify-content: center;
  height: 300px;
  color: #495057;
  p {
    margin-top: 100px;
    color: #495057;

    font-weight: 700;
    font-size: 16px;
    line-height: 19px;
    text-align: center;
  }
}
div#gridEmergencySessions {
  // .dx-datagrid-rowsview .dx-row{
  //     height:59px!important;
  // }
  .dx-datagrid-filter-row
    .dx-menu
    .dx-menu-horizontal
    .dx-menu-items-container {
    top: 0px !important;
  }
}

.inactive-session {
  text-decoration: underline;
  cursor: pointer !important;
}

.active-session {
  cursor: pointer !important;
}

.emergency-sessions-white-bg-sec {
  background-color: white;
  padding-inline-start: 0px;
  //   padding-top: 25px;
  border-radius: 8px !important;
}
</style>
